import { useLiveQuery } from 'dexie-react-hooks';
import { Cloud, CloudOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { db } from '@/lib/offline/db';
import { useOnlineStatus } from '@/lib/offline/online-status';
import { SyncDrawer } from './sync-drawer';

/**
 * Compact header chip that shows online/offline state plus the size of the
 * outgoing queue and any failed records that need attention. Clicking it
 * opens the sync drawer.
 */
export function OfflineIndicator() {
  const isOnline = useOnlineStatus();
  const pendingCount = useLiveQuery(
    () =>
      db.queue.where('status').anyOf(['pending', 'in-flight']).count(),
    [],
    0,
  );
  const failedCount = useLiveQuery(
    () =>
      db.queue.where('status').anyOf(['failed', 'requires-attention']).count(),
    [],
    0,
  );

  const variant = failedCount > 0
    ? 'destructive'
    : !isOnline || pendingCount > 0
      ? 'warning'
      : 'ok';

  const label =
    variant === 'destructive'
      ? `${failedCount} need${failedCount === 1 ? 's' : ''} attention`
      : !isOnline
        ? pendingCount > 0
          ? `Offline · ${pendingCount} queued`
          : 'Offline'
        : pendingCount > 0
          ? `Syncing ${pendingCount}`
          : 'Online';

  const Icon = variant === 'destructive'
    ? AlertTriangle
    : isOnline
      ? Cloud
      : CloudOff;

  return (
    <SyncDrawer
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className={[
            'h-8 gap-1.5 px-2.5 text-xs font-medium',
            variant === 'destructive'
              ? 'text-destructive hover:text-destructive'
              : variant === 'warning'
                ? 'text-amber-600 hover:text-amber-700'
                : 'text-muted-foreground',
          ].join(' ')}
          aria-label="Open sync drawer"
        >
          <Icon className="size-3.5" />
          {label}
        </Button>
      }
    />
  );
}
